import { useEffect, useState } from 'react';
import { Link } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { cn } from '@/lib/utils';
import { Users, Flask, PuzzlePiece, MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import { ReleasesTable } from '../components/admin/releases-table';
import { KpiGrid } from '../components/admin/kpi-grid';
import { TopUsers } from '../components/admin/top-users';
import {
  fetchAllReleases,
  fetchUsage,
  fetchUserEmails,
  summarize,
  type AdminStats,
  type ReleaseRow,
  type UsageRow,
} from '../lib/admin';
import { Alert } from '@/components/ui/alert';

type Range = '24h' | '7d' | '30d' | 'all';

const RANGE_MS: Record<Exclude<Range, 'all'>, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

export default function Admin() {
  const [range, setRange] = useState<Range>('all');
  const [usage, setUsage] = useState<UsageRow[] | null>(null);
  const [releases, setReleases] = useState<ReleaseRow[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [userEmails, setUserEmails] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setError(null);
    const since = range === 'all' ? new Date(0) : new Date(Date.now() - RANGE_MS[range]);
    try {
      const [u, r] = await Promise.all([
        fetchUsage(since),
        fetchAllReleases(50),
      ]);
      setUsage(u);
      setStats(summarize(u));
      setReleases(r);
      /* Les lignes système n'ont pas d'utilisateur : demander leur e-mail
         enverrait un null au backend. */
      const uids = [...new Set(u.map((row) => row.user_id).filter((id): id is string => id !== null))];
      fetchUserEmails(uids).then(setUserEmails).catch(() => {/* non-blocking */});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [range]);

  const costByUser = new Map<string, number>();
  for (const r of usage ?? []) {
    /* Les lignes système n'ont pas d'utilisateur : les inclure créerait un
       seau `null` en tête du classement des plus gros consommateurs. */
    if (!r.user_id) continue;
    costByUser.set(r.user_id, (costByUser.get(r.user_id) ?? 0) + Number(r.estimated_cost_usd ?? 0));
  }
  const topUsers = [...costByUser.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);

  return (
    <>
      <HeaderContent width="xl" showBurger>
        <div className="flex items-center gap-2.5 min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Admin</h1>
          <span className={HEADER_BADGE_CLASS}>Admin</span>
        </div>
      </HeaderContent>
      <main className="max-w-[1200px] mx-auto px-4 pb-12 pt-4">
        <div className="flex items-center justify-between gap-[10px] flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/users" className="inline-flex items-center gap-1.5 min-h-11 px-[14px] py-[6px] bg-naia-blue text-card border-none rounded-sm font-extrabold text-[13px] no-underline shadow-card"><Users size={16} aria-hidden />Utilisateurs</Link>
            <Link to="/admin/chat" className="inline-flex items-center gap-1.5 min-h-11 px-[14px] py-[6px] bg-card text-foreground border border-border rounded-sm font-extrabold text-[13px] no-underline shadow-card"><Flask size={16} aria-hidden />Labo d'outils</Link>
            <Link to="/admin/curriculum" className="inline-flex items-center gap-1.5 min-h-11 px-[14px] py-[6px] bg-card text-foreground border border-border rounded-sm font-extrabold text-[13px] no-underline shadow-card"><PuzzlePiece size={16} aria-hidden />Programmes structurés</Link>
            <Link to="/admin/items" className="inline-flex items-center gap-1.5 min-h-11 px-[14px] py-[6px] bg-card text-foreground border border-border rounded-sm font-extrabold text-[13px] no-underline shadow-card"><MagnifyingGlass size={16} aria-hidden />Recherche programme</Link>
            <Link to="/admin/prompts" className="inline-flex items-center gap-1.5 min-h-11 px-[14px] py-[6px] bg-card text-foreground border border-border rounded-sm font-extrabold text-[13px] no-underline shadow-card"><PencilSimple size={16} aria-hidden />Prompts système</Link>
          </div>
          <div className="flex gap-1">
            {(['24h', '7d', '30d', 'all'] as Range[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={cn(
                  "px-[10px] py-[6px] bg-transparent text-foreground border border-border rounded-sm font-extrabold cursor-pointer font-[inherit] text-[12px] min-w-11 min-h-11",
                  range === r && "bg-naia-blue text-card border-none"
                )}
              >
                {r === '24h' ? '24 h' : r === '7d' ? '7 j' : r === '30d' ? '30 j' : 'Tout'}
              </button>
            ))}
          </div>
        </div>

        {error && <Alert>{error}</Alert>}

        <KpiGrid stats={stats} />

        <SectionTitle>TOP UTILISATEURS</SectionTitle>
        <TopUsers topUsers={topUsers} userEmails={userEmails} />

        <SectionTitle>VERSIONS APK</SectionTitle>
        <ReleasesTable releases={releases} />

        <p className="mt-[10px] text-[11px] text-muted-foreground">
          Tarifs : texte (Gemini 2.5 Flash) $0.30/$2.50 par 1M tokens in/out · vocal
          (Gemini Live) $0.005/min in + $0.018/min out. Recalculés server-side.
        </p>
      </main>
    </>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-5 mb-[6px] text-[11px] font-black tracking-[1.2px]">{children}</h2>;
}
